import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {Tag} from 'folder-items/FileConstants';
import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  GenericStructuredTableColumn,
  LegacyStructuredTableRow,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Selector} from 'notebook/tables/excess/Evaluator';
import {Table, TableOpDiffMap} from 'notebook/tables/TableConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {Position} from 'samples/SampleUtil';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionProps} from 'ui-library/menus/Option';
import {BasicEvent, Optional} from 'util/Types';

export interface TransferTableRow extends LegacyStructuredTableRow {
  // Null when the row has not yet been transferred
  transferOperationId: string | null;
}

export type GenericTransferTableConfig<TIds> = {
  type: NoteLinkedObjectType.TRANSFER_TABLE_CONFIG;
  name: string;
  columns: Array<GenericStructuredTableColumn<TIds>>;
  containerSchema: ContainerSchema | null;
  containerSchemaId: string;
  parentStorableIsGrid: boolean;
  provenanceUuid: string | null;
} & NLOId<TIds>;

export type TransferTableConfig = GenericTransferTableConfig<ClientId>;
export type ToServerTransferTableConfig = GenericTransferTableConfig<ToServerId>;
export type FromServerTransferTableConfig = GenericTransferTableConfig<FromServerId>;

export type GenericTransferTableModel<TIds> = {
  name: string;
  type: NoteLinkedObjectType.TRANSFER_TABLE;
  containerSchema: ContainerSchema | null;
  transferOperationListId: string;
  parentStorableIsGrid: boolean;
  columns: Array<StructuredTableColumn>;
  provenanceUuid: string;
  rows: Array<TransferTableRow>;
} & NLOId<TIds>;

export type TransferTableModel = GenericTransferTableModel<ClientId>;
export type ToServerTransferTableModel = GenericTransferTableModel<ToServerId>;
export type FromServerTransferTableModel = GenericTransferTableModel<FromServerId>;

export interface TransferRecord {
  id: string;
  concentration: LegacyMeasurement;
  containerTransferId: string;
  outputParentStorableId: string;
  outputStageRunSampleId: string;
  gridCellRowIndex: number | null;
  gridCellColumnIndex: number | null;
}

export interface TransferOperation {
  id: string;
  stageRunId: string;
  transferRecords: Array<TransferRecord>;
}

export interface TransferOperationList {
  id: string;
  transferOperationIds: Array<string>;
}

export interface TransferTableProps extends MediocreSearchResultProps {
  icon: BenchlingIconName;
  tableTypeLabel: string;
  tableName: string;
  containerSchema: ContainerSchema;
  entryId: string;
  parentStorableIsGrid: boolean;
  provenanceUuid: string;
  registrationTables: Array<RegistrationTable>;
  rows: Array<TransferTableRow>;
  structuredTableColumns: Array<StructuredTableColumn>;
  transferOperationListId: string;
  // Editor and editor props
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  itemProps: TableItemProps;
  // SimpleTableEditable props
  cid: string;
  indentation: number;
  index: number;
  mouseDownHandler?: (index: number, e: BasicEvent) => void;
  mouseOverHandler?: (index: number, e: BasicEvent) => void;
  updateHandler: (
    index: number,
    content: {table: Table},
    selectors?: Optional<Array<Selector>>,
    tableOp?: TableOpDiffMap
  ) => void;
}

export interface TransferOperationParams {
  sourceBatchId: string | null;
  sourceBioentityId: string | null;
  sourceContainerId: string | null;
  sourcePlateId: string | null;
  sourcePosition: {rowIndex: number; columnIndex: number} | null;
  stageRunId: string | null;
  parentStorableData: {
    parentStorableId: string;
    positions: Array<Position> | null;
  };
  numberOfContainers: number;
  containerBarcodes: Array<string> | null;
  volume: LegacyMeasurement | null;
  concentration: LegacyMeasurement | null;
  containerSchemaTagsDict: {[fieldName: string]: Partial<Tag>};
}

// Keep in sync with TransferTableColumnGroup in benchling/notebook/structured_tables/models/structured_table_column.py
export enum TransferTableColumnGroup {
  SOURCE = 'SOURCE',
  TARGET = 'TARGET',
}

export const enum ContainersColumnMode {
  LEGACY, // Used in old transfer table
  SOURCE_FOR_TRANSFER_TABLE, // Source container supports searching and string resolution
  NEW_DESTINATION_FOR_TRANSFER_TABLE, // Target new container only supports plain text input
  EXISTING_DESTINATION_FOR_TRANSFER_TABLE, // Target existing container behaves like source container
  INGREDIENT_COMPONENT_LOT, // Behaves like source container
  INVENTORY_CONTAINER, // Behaves like SOURCE_FOR_TRANSFER_TABLE: supports searching & string resolution
}

export type TransferTableAddMenuItem = Pick<OptionProps<string>, 'children' | 'icon' | 'onSelect'>;
